/* ============================================================
   Game View — formatting helpers + shared primitives.
   Converted from the prototype's window globals to ES modules.
   ============================================================ */
import React from 'react';

/* ---- formatting ---- */
export function gvMoney(n, { sign = false } = {}) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  const s = '$' + abs.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (!sign) return s;
  if (v > 0) return '+' + s;
  if (v < 0) return '−' + s;
  return s;
}
export function gvK(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  const sgn = v < 0 ? '−' : '';
  if (abs >= 1000) return sgn + '$' + (abs / 1000).toFixed(1) + 'k';
  return sgn + '$' + abs.toFixed(0);
}
export function gvPct(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  return (Number(x) * 100).toFixed(0) + '%';
}
export function gvLine(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** Player club for Game Summary rows — not ``team_name`` (fixture matchup). */
export function playerClubFromQuote(q) {
  const club = String(q?.player_team || '').trim();
  if (club) return club;
  return '—';
}

/* ---- liability cell classing (ports markets-li-sign / -wgt) ---- */
export function gvLiSign(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 'gv-li--zero';
  if (n > 1e-9) return 'gv-li--pos';
  if (n < -1e-9) return 'gv-li--neg';
  return 'gv-li--zero';
}
export function gvLiWeight(v, maxAbs) {
  const av = Number.isFinite(Number(v)) ? Math.abs(Number(v)) : 0;
  if (!Number.isFinite(maxAbs) || maxAbs < 1e-9) return 'gv-li--low';
  const r = av / maxAbs;
  if (r >= 0.72) return 'gv-li--high';
  if (r >= 0.22) return 'gv-li--mid';
  return 'gv-li--low';
}
export function gvLiClass(v, maxAbs) {
  return `${gvLiSign(v)} ${gvLiWeight(v, maxAbs)}`;
}

/**
 * Worst-case net liability for the book across an over/under pair.
 * Positive = book exposed; so take Math.max (same as Dashboard risk card),
 * not larger |abs| (which can pick a large under win as "worst").
 */
export function gvDominant(lo, lu) {
  const o = Number.isFinite(Number(lo)) ? Number(lo) : 0;
  const u = Number.isFinite(Number(lu)) ? Number(lu) : 0;
  return Math.max(o, u);
}

/* roll a market's lines into one aggregate */
export function gvRollMarket(m) {
  let feeOver = 0, feeUnder = 0, lo = 0, lu = 0, n = 0, nOver = 0, nUnder = 0;
  let sw = 0, sn = 0, swOver = 0, snOver = 0, swUnder = 0, snUnder = 0, combo = 0;
  let slipOverNum = 0, slipOverDen = 0, slipUnderNum = 0, slipUnderDen = 0;
  for (const ln of m.lines) {
    feeOver += ln.fee_over || 0;
    feeUnder += ln.fee_under || 0;
    lo += ln.liability_net_over || 0;
    lu += ln.liability_net_under || 0;
    n += ln.n_entries || 0;
    nOver += ln.n_entries_over || 0;
    nUnder += ln.n_entries_under || 0;
    combo += ln.combo || 0;
    if (ln.avg_sharp != null) { sw += ln.avg_sharp * (ln.n_entries || 1); sn += (ln.n_entries || 1); }
    if (ln.avg_sharp_over != null) { swOver += ln.avg_sharp_over * (ln.n_entries_over || 1); snOver += (ln.n_entries_over || 1); }
    if (ln.avg_sharp_under != null) { swUnder += ln.avg_sharp_under * (ln.n_entries_under || 1); snUnder += (ln.n_entries_under || 1); }
    if (ln.avg_slip_over != null && ln.n_slips_over) { slipOverNum += ln.avg_slip_over * ln.n_slips_over; slipOverDen += ln.n_slips_over; }
    if (ln.avg_slip_under != null && ln.n_slips_under) { slipUnderNum += ln.avg_slip_under * ln.n_slips_under; slipUnderDen += ln.n_slips_under; }
  }
  return {
    fee: feeOver + feeUnder, fee_over: feeOver, fee_under: feeUnder,
    lo, lu, n, n_over: nOver, n_under: nUnder, combo,
    avg_sharp: sn ? sw / sn : null,
    avg_sharp_over: snOver ? swOver / snOver : null,
    avg_sharp_under: snUnder ? swUnder / snUnder : null,
    n_slips_over: slipOverDen,
    n_slips_under: slipUnderDen,
    avg_slip_over: slipOverDen ? slipOverNum / slipOverDen : null,
    avg_slip_under: slipUnderDen ? slipUnderNum / slipUnderDen : null,
    dom: gvDominant(lo, lu),
  };
}
export function gvRollPlayer(p) {
  let feeOver = 0, feeUnder = 0, lo = 0, lu = 0, n = 0, nOver = 0, nUnder = 0;
  let sw = 0, sn = 0, swOver = 0, snOver = 0, swUnder = 0, snUnder = 0, combo = 0;
  let slipOverNum = 0, slipOverDen = 0, slipUnderNum = 0, slipUnderDen = 0;
  for (const m of p.markets) {
    const r = gvRollMarket(m);
    feeOver += r.fee_over; feeUnder += r.fee_under;
    lo += r.lo; lu += r.lu; n += r.n; nOver += r.n_over; nUnder += r.n_under; combo += r.combo;
    if (r.avg_sharp != null) { sw += r.avg_sharp * r.n; sn += r.n; }
    if (r.avg_sharp_over != null) { swOver += r.avg_sharp_over * r.n_over; snOver += r.n_over; }
    if (r.avg_sharp_under != null) { swUnder += r.avg_sharp_under * r.n_under; snUnder += r.n_under; }
    if (r.avg_slip_over != null && r.n_slips_over) { slipOverNum += r.avg_slip_over * r.n_slips_over; slipOverDen += r.n_slips_over; }
    if (r.avg_slip_under != null && r.n_slips_under) { slipUnderNum += r.avg_slip_under * r.n_slips_under; slipUnderDen += r.n_slips_under; }
  }
  return {
    fee: feeOver + feeUnder, fee_over: feeOver, fee_under: feeUnder,
    lo, lu, n, n_over: nOver, n_under: nUnder, combo,
    avg_sharp: sn ? sw / sn : null,
    avg_sharp_over: snOver ? swOver / snOver : null,
    avg_sharp_under: snUnder ? swUnder / snUnder : null,
    n_slips_over: slipOverDen,
    n_slips_under: slipUnderDen,
    avg_slip_over: slipOverDen ? slipOverNum / slipOverDen : null,
    avg_slip_under: slipUnderDen ? slipUnderNum / slipUnderDen : null,
    dom: gvDominant(lo, lu),
  };
}

/* sharp → bg color (ports EntriesPage getSharpColor) */
export function gvSharpColor(s) {
  if (s == null) return null;
  const t = Math.max(0, Math.min(1, Number(s)));
  const r = Math.round(16 + (239 - 16) * t);
  const g = Math.round(185 - (185 - 68) * t);
  const b = Math.round(129 - (129 - 68) * t);
  return `rgb(${r},${g},${b})`;
}
export function gvSharpText(s) {
  const t = s == null ? null : Math.max(0, Math.min(1, Number(s)));
  return t != null && t >= 0.55 ? '#fff' : 'inherit';
}

/* ---- tiny components ---- */
export function GvBadge({ kind, children }) {
  return <span className={`gv-badge gv-badge--${kind}`}>{children}</span>;
}

export function GvGateBadge({ gate }) {
  if (gate === 'suspended') return <GvBadge kind="sus">Susp</GvBadge>;
  if (gate === 'limited') return <GvBadge kind="limited">Limited</GvBadge>;
  if (gate === 'gated') return <GvBadge kind="gate">Blocked</GvBadge>;
  return <GvBadge kind="open">Open</GvBadge>;
}

export function GvComboChip({ n }) {
  if (!n) return null;
  return (
    <span className="gv-combo-chip" title={`${n} entries on this line are combo legs`}>
      <span className="dot" />{n}
    </span>
  );
}

export function GvSharpCell({ s }) {
  const bg = gvSharpColor(s);
  return (
    <td className="num" style={bg ? { backgroundColor: bg, color: gvSharpText(s) } : undefined}>
      {s != null ? Number(s).toFixed(2) : '—'}
    </td>
  );
}

export function GvSideMetric({ over, under, format, sharp = false }) {
  const renderValue = (value) => {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    return format ? format(Number(value)) : String(value);
  };
  const styleFor = (value) => {
    if (!sharp || value == null) return undefined;
    const backgroundColor = gvSharpColor(value);
    return backgroundColor ? { backgroundColor, color: gvSharpText(value) } : undefined;
  };
  return (
    <span className="gv-side-metric">
      <span className="gv-side-part">
        <span className="gv-side-label gv-side-label--over">O</span>
        <span className={`gv-side-value${sharp ? ' gv-side-value--sharp' : ''}`} style={styleFor(over)}>
          {renderValue(over)}
        </span>
      </span>
      <span className="gv-side-part">
        <span className="gv-side-label gv-side-label--under">U</span>
        <span className={`gv-side-value${sharp ? ' gv-side-value--sharp' : ''}`} style={styleFor(under)}>
          {renderValue(under)}
        </span>
      </span>
    </span>
  );
}

export function GvSideMetricCell(props) {
  return <td className="num"><GvSideMetric {...props} /></td>;
}

/* signed liability span */
export function GvLi({ v, max, sign = true }) {
  return <span className={`gv-num ${gvLiClass(v, max)}`}>{gvMoney(v, { sign })}</span>;
}

/* Open the selected market in the existing dashboard tab set. */
export function gvObHref(fixtureId, playerId, marketId, line) {
  const market = `${fixtureId}:${playerId}:${marketId}`;
  const params = new URLSearchParams({ market, tab: 'orderBook' });
  if (line != null) params.set('line', String(line));
  return `/dashboard?${params.toString()}`;
}
